// src/utils/notifications.js
// Handles FCM push notification permission, token storage, and foreground messages.

import { getToken, onMessage, deleteToken } from "firebase/messaging";
import { doc, setDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db, messagingPromise } from "../firebase";

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

/**
 * Requests notification permission, obtains an FCM token, and persists it
 * to the user's profile doc so Cloud Functions can send pushes to this device.
 * Safe to call multiple times — arrayUnion prevents duplicates in Firestore.
 */
export async function registerPushToken(userId) {
  if (!userId) return;
  if (!("Notification" in window)) return;
  if (!VAPID_KEY) {
    console.warn("FCM: REACT_APP_FIREBASE_VAPID_KEY is not set — skipping push registration.");
    return;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return;

    const messaging = await messagingPromise;
    if (!messaging) return;

    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (!token) return;

    await setDoc(
      doc(db, "profiles", userId),
      { fcmTokens: arrayUnion(token) },
      { merge: true }
    );
  } catch (err) {
    // Non-fatal — the app works without push notifications.
    console.warn("FCM: token registration failed:", err);
  }
}

/**
 * Removes all stored FCM tokens from the user's profile so they stop receiving
 * push notifications. Also deletes the current device token from FCM.
 * The browser permission itself can only be revoked in browser settings.
 */
export async function unregisterPushToken(userId) {
  if (!userId) return;
  try {
    const messaging = await messagingPromise;
    if (messaging) {
      try { await deleteToken(messaging); } catch { /* token may already be gone */ }
    }
    await updateDoc(doc(db, "profiles", userId), { fcmTokens: [] });
  } catch (err) {
    console.warn("FCM: token unregistration failed:", err);
  }
}

/**
 * Listens for foreground messages (when the app tab is open and focused).
 * Shows a native Notification since the service worker won't fire for foreground.
 * Returns the unsubscribe function.
 */
export async function listenForegroundMessages() {
  const messaging = await messagingPromise;
  if (!messaging) return () => {};

  return onMessage(messaging, (payload) => {
    if (Notification.permission !== "granted") return;
    const { title, body } = payload.notification || {};
    if (!title) return;
    new Notification(title, {
      body: body || "",
      icon: "/logo192.png",
      badge: "/logo192.png",
      tag: payload.data?.tag || "marelle-memories-fg",
    });
  });
}
